<template>
  <v-container fluid class="pb-15">
    <v-row>
      <v-col v-if="$can('create', 'sites')">
        <v-btn color="primary" to="/site/create">
          <v-icon start>mdi-plus-thick</v-icon>New Site
        </v-btn>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto" md="6" sm="6">
        <v-text-field
          v-model="search"
          label="Search"
          variant="outlined"
          density="compact"
          clearable
        ></v-text-field>
      </v-col>
    </v-row>

    <v-progress-linear :active="loading" :indeterminate="loading" absolute />
    <div class="mt-8">
      <v-data-table
        :headers="headers"
        :items="sites"
        :hide-default-footer="sites.length <= 10"
        :items-per-page="-1"
        :search="search"
        v-model:sort-by="sitesSortBy"
        :options="tableOptions"
        class="elevation-1"
      >
        <!--Headers slot-->
        <template
          v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }"
        >
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="column.class"
              @click="() => toggleSort(column)"
            >
              {{ column.title }}
              <template v-if="isSorted(column)">
                <v-icon :icon="getSortIcon(column)" size="sm" />
              </template>
            </th>
          </tr>
        </template>
        <!--Items slot-->
        <template v-slot:item="{ item }">
          <tr
            @mouseenter="(e) => { hoveredSiteId = item.siteId; updateTooltipPos(e); }"
            @mouseleave="hoveredSiteId = null"
            @mousemove="updateTooltipPos"
          >
            <td>{{ item.name }}</td>
            <td>{{ item.floors }}</td>
            <td>{{ item.occupants }}</td>
            <td>{{ item.squareFootage }}</td>
            <td>{{ item.kwhCost }}</td>
            <td>{{ item.latitude }}</td>
            <td>{{ item.longitude }}</td>
            <td>
              <v-btn
                :to="`/site/edit/${item.siteId}`"
                icon="mdi-pencil"
                size="medium"
                variant="text"
              >
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <!-- <div class="bottom-bar px-10" v-if="!loading">
      <v-row align="center" class="ma-0">
        <v-col />
        <v-col cols="auto">
          <v-btn
            variant="outlined"
            color="primary"
            prepend-icon="mdi-download"
            @click="exportCSV({ exclude: 'actions' })"
          >
            Export CSV
          </v-btn>
        </v-col>
      </v-row>
    </div> -->
  </v-container>

  <Teleport to="body">
    <div
      v-if="hoveredSiteId !== null"
      class="site-id-tooltip"
      :style="{ top: tooltipY + 'px', left: tooltipX + 'px' }"
    >
      Site ID: <span class="site-id-value">{{ hoveredSiteId }}</span>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue';
// import { useDataExport } from '@/Core.UI.Domain/Composables/useDataExport';

const loading = ref(true);
const search = ref('');
const sites = ref([]);
const sitesSortBy = ref([{ key: 'name', order: 'asc' }]);
const tableOptions = { itemsPerPage: 10 };

const hoveredSiteId = ref(null);
const tooltipX = ref(0);
const tooltipY = ref(0);

let rafId = null;

function updateTooltipPos(e) {
  if (rafId) return;
  rafId = requestAnimationFrame(() => {
    tooltipX.value = e.clientX + 14;
    tooltipY.value = e.clientY + 14;
    rafId = null;
  });
}

const headers = [
  {
    title: 'Site Name',
    class: 'text-primary font-weight-bold text-subtitle-1',
    key: 'name',
    sortable: true,
  },
  {
    title: 'Floors',
    class: 'text-primary font-weight-bold text-subtitle-1',
    key: 'floors',
    sortable: true,
  },
  {
    title: 'Occupants',
    class: 'text-primary font-weight-bold text-subtitle-1',
    key: 'occupants',
    sortable: true,
  },
  {
    title: 'Square Footage',
    class: 'text-primary font-weight-bold text-subtitle-1',
    key: 'squareFootage',
    sortable: true,
  },
  {
    title: 'kWh Cost',
    class: 'text-primary font-weight-bold text-subtitle-1',
    key: 'kwhCost',
    sortable: true,
  },
  {
    title: 'Latitude',
    class: 'text-primary font-weight-bold text-subtitle-1',
    key: 'latitude',
    sortable: true,
  },
  {
    title: 'Longitude',
    class: 'text-primary font-weight-bold text-subtitle-1',
    key: 'longitude',
    sortable: true,
  },
  {
    title: 'Actions',
    class: 'text-primary font-weight-bold text-subtitle-1',
    key: 'actions',
    sortable: false,
  },
];

onMounted(async () => {
  const response = await window.axios.get('/v1/sites');
  response.data.data.forEach((site) => {
    site.latitude = Number(site.latitude).toFixed(5);
    site.longitude = Number(site.longitude).toFixed(5);
  });
  sites.value = response.data.data;
  loading.value = false;
});

// const { exportCSV } = useDataExport(sites, headers, 'sites-list');
</script>

<style scoped>
.bottom-bar {
  position: fixed;
  bottom: 0;
  left: var(--v-layout-left, 0);
  right: 0;
  z-index: 10;
  max-height: 70px;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
  padding-top: 0;
  padding-bottom: 0;
}

.site-id-tooltip {
  position: fixed;
  background: rgba(50, 50, 50, 0.9);
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 9999;
  white-space: nowrap;
}

.site-id-value {
  font-size: 14px;
  font-weight: 700;
}
</style>
